import Input from '@/shared/components/rebranded/Input/Input';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { ChangeEvent } from 'react';
import { Label } from '../../../shared/components/Label/Label';
import { FieldProps, Schema, StringSchema } from '../../types';
import { joinErrors } from '../../validation/utils';
import { hasErrors } from '../../validation/validateSchema';
import { ValidationStringFormat } from '../../validation/validators';
import { OptionalHint } from '../OptionalHint/OptionalHint';
import { Wrapper } from './styled';

const FORMAT_MAP: { [key in ValidationStringFormat]: string } = {
  'phone-number': 'tel',
  email: 'email',
  'optional-email': 'email',
  nin: 'tel',
  'account-number': 'tel',
  'credit-card-number': 'tel',
  date: 'date',
  'date-not-future': 'date',
  'date-time': 'date',
  'date-time-not-future': 'date',
  'organization-number': 'tel',
  'license-plate-number': 'text',
  'birth-date': 'text'
};

const resolveInputType = (schema: Schema) => {
  if (schema.type === 'string' && schema.format) {
    return FORMAT_MAP[schema.format] || 'text';
  }

  return 'text';
};

export type TextInputFieldProps = FieldProps<StringSchema, string | null> & {
  suffix?: string;
  suffixAriaLabel?: string;
};

export const TextInputField = ({
  schema,
  name,
  value,
  touched,
  errors,
  id,
  onBlur,
  onChange,
  forceShowError,
  readOnly,
  isFieldRequired,
  suffix,
  suffixAriaLabel
}: TextInputFieldProps) => {
  const { getText } = useTranslations();
  const $uiOptions = schema.$uiOptions || {};
  const showError = !!((touched || forceShowError) && hasErrors(errors));
  const autoComplete = $uiOptions.autoComplete === false ? 'off' : 'on';

  const handleBlur = () => {
    return onBlur({
      target: {
        name,
        touched: true
      }
    });
  };

  const handleChange = ({
    target: { value: changeValue }
  }: ChangeEvent<any>) => {
    onChange({
      target: {
        name,
        value: changeValue
      }
    });
  };

  const resolvedInputType = resolveInputType(schema);

  const error = showError ? joinErrors(errors, getText) : undefined;

  const label = (
    <Label title={schema.title} description={schema.description} id={id} />
  );

  const isLargeField = $uiOptions.fieldSize === 'large';
  const isMultiline = $uiOptions.multiline ?? false;
  const largeInputField = (isLargeField || isMultiline) ?? false;
  const useDefaultSize = !$uiOptions.fieldSize;

  const isHintRequired = isFieldRequired || isFieldRequired === undefined;
  const showHint = isHintRequired ? (
    $uiOptions.hint
  ) : (
    <OptionalHint hint={$uiOptions.hint} />
  );

  return (
    <Wrapper large={isLargeField} multiline={isMultiline}>
      <Input
        id={id}
        inputProps={{
          type: resolvedInputType,
          autoComplete,
          placeholder: $uiOptions.placeholder
        }}
        name={name}
        label={label}
        hint={showHint}
        hintProps={{
          maxLines: 3,
          readMoreLabel: 'les mer',
          readLessLabel: 'les mindre'
        }}
        error={error}
        small={$uiOptions.fieldSize === 'xsmall'}
        medium={$uiOptions.fieldSize === 'small'}
        large={
          $uiOptions.fieldSize === 'medium' ||
          (!largeInputField && useDefaultSize)
        }
        fluid={largeInputField}
        as={$uiOptions.multiline ? 'textarea' : 'input'}
        rows={isMultiline && $uiOptions.rows ? $uiOptions.rows : 6}
        value={value == null ? '' : value}
        type="text"
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={readOnly}
        suffix={$uiOptions.suffix ?? suffix}
        suffixProps={{
          'aria-label': $uiOptions.suffixAriaLabel ?? suffixAriaLabel
        }}
      />
    </Wrapper>
  );
};
